import React, { useMemo } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import { Field, useField, useFormState } from 'react-final-form';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useAdditionalFields from '../../../../hooks/useAdditionalFields';
import { AI_STUDIO_FIELDS } from '../../../../../../constants';
import TextInput from '../../../../../../ai-studio/fields/text/TextInput';
import FileInput from '../../../../../../ai-studio/fields/file/FileInput';
import ConvertInput from '../../../../../../ai-studio/fields/convert/ConvertInput';
import ChatInput from '../../../../../../ai-studio/fields/chat/ChatInput';
import TooltipButton from '../../../../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../../../../components/common-button/CommonButton';
import { ICONS } from '../../../../../../icons';
import Icon from '../../../../../../icons/Icon';
import { selectedMediaSelector } from '../../../../../../redux/ai-studio/aiStudioSelectors';
import DropdownInput from '../../../../../../ai-studio/fields/dropdown/DropdownInput';
import SwitchInput from '../../../../../../ai-studio/fields/switch/SwitchInput';

import scss from './Fields.module.scss';

function Fields({ onSubmit }) {
  const { submitting } = useFormState();
  const { t } = useTranslation('studio');

  const selectedMedia = useSelector(selectedMediaSelector);

  const isDisabled = useMemo(() => !selectedMedia || submitting, [selectedMedia, submitting]);

  const fields = useAdditionalFields(selectedMedia?.mediaType);

  const {
    input: { value: selectedAction },
  } = useField('action');
  const { values: formValues } = useFormState({ subscription: { values: true } });
  const { input: attachmentsInput, meta: attachmentsMeta } = useField('attachments', {
    subscription: { value: true, touched: true, error: true, submitFailed: true },
  });

  const fieldComponents = useMemo(() => {
    const actionFields = fields[selectedAction] ?? [];

    return actionFields.filter((field) => {
      if (!field.showWhen) {
        return true;
      }

      return formValues[field.showWhen.field] === field.showWhen.value;
    });
  }, [fields, selectedAction, formValues]);

  return (
    <div className={scss.wrapper}>
      <div className={scss.fieldsWrapper}>
        {fieldComponents.map((field) => (
          <div key={field.name} className={clsx(scss.field, scss[`size-${field.size}`])}>
            {field.type === AI_STUDIO_FIELDS.TEXT && (
              <TextInput
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                disabled={isDisabled}
                onSubmit={onSubmit}
              />
            )}
            {field.type === AI_STUDIO_FIELDS.FILE && (
              <FileInput
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                acceptedFileTypes={field.acceptedFileTypes}
                disabled={isDisabled}
              />
            )}
            {field.type === AI_STUDIO_FIELDS.DROPDOWN && (
              <DropdownInput
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                options={field.options}
                disabled={isDisabled}
              />
            )}
            {field.type === AI_STUDIO_FIELDS.CONVERT && (
              <ConvertInput
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                disabled={isDisabled}
                onSubmit={onSubmit}
              />
            )}
            {field.type === AI_STUDIO_FIELDS.SWITCH && (
              <SwitchInput name={field.name} label={field.label} disabled={isDisabled} subLabels={field.subLabels} />
            )}
            {field.type === AI_STUDIO_FIELDS.CHAT && (
              <Field name={field.name}>
                {({ input, meta }) => {
                  const hasMessageError = meta.touched && meta.error;
                  const hasAttachmentsError =
                    field.isFileRequired &&
                    attachmentsMeta.error &&
                    (attachmentsMeta.touched || attachmentsMeta.submitFailed);

                  return (
                    <ChatInput
                      value={input.value}
                      onChange={input.onChange}
                      onFocus={input.onFocus}
                      onBlur={input.onBlur}
                      attachments={attachmentsInput.value ?? []}
                      onChangeAttachments={attachmentsInput.onChange}
                      //
                      isSending={submitting}
                      isError={hasMessageError || hasAttachmentsError}
                      errorText={hasMessageError ? t(meta.error) : hasAttachmentsError ? t(attachmentsMeta.error) : ''}
                      onSubmit={onSubmit}
                      //
                      disabled={isDisabled}
                      disableFileAttachments={field.disableAttachments}
                      acceptedFileTypes={field.acceptedFileTypes}
                    />
                  );
                }}
              </Field>
            )}
          </div>
        ))}
      </div>

      <div className={scss.sendButtonWrapper}>
        <TooltipButton styleColor={BUTTON_STYLES.PRIMARY} maxWidth={40} onClick={onSubmit} disabled={isDisabled}>
          {submitting ? (
            <RotatingLines strokeColor='currentColor' height={10} width={20} />
          ) : (
            <Icon icon={ICONS.ArrowUp} />
          )}
        </TooltipButton>
      </div>
    </div>
  );
}

export default Fields;
